import { Tooltip } from '@chakra-ui/react';
import { saveAs } from 'file-saver';
import * as React from 'react';
import { Button, IButtonProps, useSnackbar } from 'ui-components';
import {
  FEATURE_FLAGS,
  PDF_DOCUMENT_TYPE,
  SNACKBAR_ERROR_GENERIC_OPTIONS,
} from '~/constants';
import { logger } from '~/core/logger/logger';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { pdfApi } from '~/pdfApi';

interface Props extends IButtonProps {
  children?: React.ReactNode;
  fileName: string;
  data: any;
  onSuccess?: () => void;
  pdfType: PDF_DOCUMENT_TYPE;
  toolTip?: React.ReactNode;
}

export const ExportPdfButton: React.FC<Props> = ({
  children = 'Download PDF',
  fileName = 'document.pdf',
  data: pdfData,
  onSuccess,
  pdfType,
  toolTip = null,
  ...buttonProps
}) => {
  const snackbar = useSnackbar();
  const [isLoading, setIsLoading] = React.useState(false);

  const isMetricsCollaborationFFEnbled = useFeature(
    FEATURE_FLAGS.foamMetricsCollaborations,
  ).isEnabled;

  if (!isMetricsCollaborationFFEnbled) {
    delete pdfData.content.isCollaboration;
  }

  const handleClick = async () => {
    setIsLoading(true);
    try {
      const pdfStream = await pdfApi.create({
        pdfType,
        data: pdfData,
      });
      const data = pdfStream.data;

      saveAs(data, fileName);
      logger
        .withMetadata({
          contentId: pdfData?.content?.id,
          section: 'pdf-generation',
        })
        .info('Content PDF downloaded successfully');

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      snackbar.error(SNACKBAR_ERROR_GENERIC_OPTIONS);
      logger
        .withMetadata({
          contentId: pdfData.content.id,
          section: 'pdf-generation',
          error,
        })
        .error('Error exporting the content PDF');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Tooltip label={toolTip} backgroundColor="white" rounded="md">
      <Button
        aria-label="Download PDF"
        isLoading={isLoading}
        onClick={handleClick}
        {...buttonProps}
      >
        {children}
      </Button>
    </Tooltip>
  );
};
